import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { IChatMessage, ISendChatMessage } from "../types";
import { loginToAskDost } from "../services/askDostService";

interface IUseChatActionsProps {
  messages: IChatMessage[];
  addMessage: (message: IChatMessage) => void;
  streamChat: (data: ISendChatMessage) => Promise<void>;
  translate: (text: string, options?: any) => string;
  onStreamStart?: () => void;
  onStreamComplete?: () => void;
  onStreamError?: (error: Error) => void;
}

interface IChatActionsReturn {
  handleSendMessage: (data: ISendChatMessage) => Promise<void>;
  handleRegenerate: (messageId: string) => void;
}

export const useChatActions = ({
  messages,
  addMessage,
  streamChat,
  translate,
  onStreamStart,
  onStreamComplete,
  onStreamError,
}: IUseChatActionsProps): IChatActionsReturn => {
  const { enqueueSnackbar } = useSnackbar();

  const handleSendMessage = useCallback(
    async (data: ISendChatMessage) => {
      if (!data.message.trim()) return;

      if (!navigator.onLine) {
        enqueueSnackbar(translate("offline_error"), { variant: "error" });
        return;
      }
      const isNewConversation = messages.length === 0;

      onStreamStart?.();

      const userMessage: IChatMessage = {
        id: `user-${Date.now()}`,
        content: data.message,
        sender: "user",
        attachments: data.attachments,
      };

      addMessage(userMessage);

      const assistantMessage: IChatMessage = {
        id: `ai-${Date.now()}`,
        content: "",
        sender: "ai",
        blocks: [],
      };

      addMessage(assistantMessage);

      try {
        await streamChat(
          isNewConversation ? { ...data, new_conversation: true } : data,
        );
        onStreamComplete?.();
      } catch (error) {
        console.error("Error sending message:", error);
        if (error instanceof Error && error.message.includes("401")) {
          await loginToAskDost();
        }
        onStreamError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    [
      messages,
      addMessage,
      streamChat,
      translate,
      enqueueSnackbar,
      onStreamStart,
      onStreamComplete,
      onStreamError,
    ],
  );

  const handleRegenerate = useCallback(
    (messageId: string) => {
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) return;

      const userMessage = messages[messageIndex - 1];
      if (!userMessage || userMessage.sender !== "user") return;

      if (!navigator.onLine) {
        enqueueSnackbar(translate("offline_error"), { variant: "error" });
        return;
      }

      onStreamStart?.();
      addMessage(userMessage);

      const assistantMessage: IChatMessage = {
        id: `ai-${Date.now()}`,
        content: "",
        sender: "ai",
        blocks: [],
      };

      addMessage(assistantMessage);

      streamChat({
        message: userMessage.content,
        attachments: userMessage.attachments || [],
      }).catch(async (error) => {
        console.error("Error regenerating message:", error);

        let errorMessage = translate("something_went_wrong");
        if (error instanceof Error) {
          errorMessage = error.message;
          if (error.message.includes("401")) {
            await loginToAskDost();
          }
        }

        const errorBlock: IChatMessage = {
          id: `ai-${Date.now()}`,
          content: "",
          sender: "ai",
          blocks: [
            {
              id: `block-${Date.now()}`,
              type: "error",
              content: errorMessage,
            },
          ],
        };
        addMessage(errorBlock);
        onStreamError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
      });
    },
    [
      messages,
      addMessage,
      streamChat,
      translate,
      enqueueSnackbar,
      onStreamStart,
      onStreamError,
    ],
  );

  return { handleSendMessage, handleRegenerate };
};
